import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { Package, X } from 'lucide-react';
import { downloadStudyBuild, readErrorMessage } from '../../services/studyPromotionApi';

const ExportCandidateModal = ({ isOpen, onClose, studyId, studyCode, studyTitle }) => {
  const [label, setLabel] = useState('');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error('Enter a candidate label, e.g. "DR1-rc1".');
      return;
    }
    setExporting(true);
    try {
      await downloadStudyBuild(studyId, trimmed, `${studyCode || studyId}-${trimmed}.fxbuild.json`);
      toast.success(`Candidate "${trimmed}" exported.`);
      setLabel('');
      onClose();
    } catch (err) {
      toast.error(await readErrorMessage(err, 'Export failed.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => !exporting && onClose()} className="relative z-50">
      <div className="fixed inset-0 bg-gray-950/40 dark:bg-black/70" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
          <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-5">
            <Dialog.Title className="flex items-center gap-2.5 text-base font-semibold text-gray-900 dark:text-gray-50">
              <Package className="h-[18px] w-[18px] text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
              Export Candidate Build
            </Dialog.Title>
            <button
              onClick={() => !exporting && onClose()}
              className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>

          <div className="px-6 pb-5 pt-2">
            <p className="mb-4 text-[13.5px] leading-relaxed text-gray-600 dark:text-gray-300">
              Downloads the complete design-time build of{' '}
              <span className="font-medium text-gray-900 dark:text-gray-50">{studyTitle}</span> as a checksummed candidate
              package (<span className="font-mono text-[12.5px]">.fxbuild.json</span>), ready to deploy into another environment. No
              participant, response, or user data is included.
            </p>

            <label className="label">
              Candidate label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleExport()}
              placeholder='e.g. "DR1-rc1"'
              maxLength={255}
              autoFocus
              className="input"
            />
            <p className="hint">
              Names this candidate through review and deployment, e.g. a release-candidate tag.
            </p>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
            <button
              onClick={() => !exporting && onClose()}
              disabled={exporting}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !label.trim()}
              className="btn btn-primary"
            >
              <Package className="h-4 w-4" />
              {exporting ? 'Exporting...' : 'Export Package'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ExportCandidateModal;
